/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-27 10:33:30
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, TextField, Popover, Button, Stack, Select, Tag, Tabs } from '@shopify/polaris';
import { SearchMinor, FilterMajor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';

import {
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES,
  RECOMMENDATION_WIDGET_STATUS
} from 'src/constants/recommendation';
import type { ComposedProps, State } from './filters-bar.type';

const RefreshIcon = (
  <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.41667 1.75C7.57692 1.75 8.67592 2.12917 9.58417 2.81925L7.48183 4.7985L12.8293 5.37192L12.5778 0L10.4458 2.00783C9.30825 1.08617 7.90125 0.583333 6.41667 0.583333C2.87875 0.583333 0 3.46208 0 7H1.16667C1.16667 4.10492 3.52158 1.75 6.41667 1.75Z"
      fill="#2C6ECB"
    />
    <path
      d="M6.41649 12.25C5.25624 12.25 4.15724 11.8708 3.24899 11.1807L5.35132 9.2015L0.00390625 8.62808L0.255323 14L2.38741 11.9922C3.52491 12.9138 4.93191 13.4167 6.41649 13.4167C9.95441 13.4167 12.8332 10.5379 12.8332 7H11.6665C11.6665 9.89508 9.31157 12.25 6.41649 12.25Z"
      fill="#2C6ECB"
    />
  </svg>
);

class FiltersBar extends Component<ComposedProps, State> {
  state = {
    popoverActive: false,
    themeOptions: [],
    recommendationTypeOptions: [],
    themeOption: '',
    recommendationTypeOption: ''
  };

  componentDidMount() {
    const { i18n } = this.props;
    const recommendationTypeOptions = _.map(RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES, item => {
      return { value: item.value, label: i18n.translate(item.name) };
    });
    this.setState({ recommendationTypeOptions });
  }

  componentWillReceiveProps(nextProps) {
    const { getThemesLoading, syncWidgetsFlag } = this.props;
    const {
      themes: nextThemes,
      getThemesLoading: nextGetThemesLoading,
      loadThemesOption,
      setLoadThemesOption,
      syncWidgetsFlag: nextSyncWidgetsFlag
    } = nextProps;

    if ((getThemesLoading && !nextGetThemesLoading) || loadThemesOption) {
      const themeOptions = nextThemes
        ? _.map(nextThemes, item => ({
            value: item.id.toString(),
            label: `${item.name}${
              item.published
                ? this.props.i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.suffixThemeLive')
                : ''
            }`
          }))
        : [];

      this.setState({ themeOptions });
      setLoadThemesOption(false);
    }

    if (syncWidgetsFlag !== nextSyncWidgetsFlag) {
      // Reset filter
      this.setState({ themeOption: '', recommendationTypeOption: '' });
    }
  }

  // Render apply filter for filter
  renderApplyFilter = () => {
    const { filterOptions, handleFilter } = this.props;
    const { themeOption = '', recommendationTypeOption = '' } = filterOptions;

    const appliedFilters = [];
    if (!_.isEmpty(themeOption)) {
      const key = 'theme';
      appliedFilters.push({
        key,
        label: this.disambiguateLabel(key),
        onRemove: () => {
          // Handle remove
          this.setState({ themeOption: '' });
          handleFilter({ themeOption: '', recommendationTypeOption });
        }
      });
    }
    if (!_.isEmpty(recommendationTypeOption)) {
      const key = 'type';
      appliedFilters.push({
        key,
        label: this.disambiguateLabel(key),
        onRemove: () => {
          // Handle remove
          this.setState({ recommendationTypeOption: '' });
          handleFilter({ themeOption, recommendationTypeOption: '' });
        }
      });
    }

    if (_.isEmpty(appliedFilters)) {
      return '';
    }

    const tagMarkUp = _.map(appliedFilters, appliedFilter => {
      const { key, label, onRemove } = appliedFilter;
      return (
        <Tag key={key} onRemove={onRemove}>
          {label}
        </Tag>
      );
    });

    return (
      <div className="BC-Filters__TagsContainer">
        <Stack spacing="tight">{tagMarkUp}</Stack>
      </div>
    );
  };

  render() {
    const {
      i18n,
      queryValue,
      onQueryChange,
      handleKeyUp,
      handleKeyDown,
      selectedTab,
      handleChangeTab,
      syncWidgets
    } = this.props;
    const {
      popoverActive,
      themeOptions,
      recommendationTypeOptions,
      themeOption,
      recommendationTypeOption
    } = this.state;
    const activator = (
      <div className="btn-filter-rule">
        <Button plain onClick={this.togglePopoverActive} icon={FilterMajor}>
          {i18n.translate('AdminHTML.Recommendation.WidgetDashboard.filter_Label')}
        </Button>
      </div>
    );
    const tabs = _.map(RECOMMENDATION_WIDGET_STATUS, item => {
      return {
        id: item.value,
        content: i18n.translate(item.label),
        panelID: item.value
      };
    });
    return (
      <div className="BC-Filters">
        <div className="BC-Filters-ConnectedFilterControl">
          <div className="BC-Filters-ConnectedFilterControl__LeftContainer">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={handleChangeTab}></Tabs>
          </div>
          <div className="BC-Filters-ConnectedFilterControl__RightContainer">
            <div className="search" onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
              <TextField
                onChange={onQueryChange}
                value={queryValue}
                prefix={<Icon source={SearchMinor} color="base" />}
                placeholder={i18n.translate(
                  'AdminHTML.Recommendation.WidgetDashboard.searchField__Placeholder'
                )}
              />
            </div>
            <div className="action filter">
              <Popover
                preventCloseOnChildOverlayClick
                preferredAlignment="left"
                active={popoverActive}
                activator={activator}
                onClose={this.closePopover}
                sectioned>
                <Popover.Pane>
                  <Popover.Section>
                    <div className="BC-Popover__Recommendation-Filters-Options">
                      <Stack vertical>
                        <Select
                          label={i18n.translate(
                            'AdminHTML.Recommendation.WidgetDashboard.filter_Theme_Label'
                          )}
                          placeholder={i18n.translate(
                            'AdminHTML.Recommendation.WidgetDashboard.filter_Theme_Placeholder'
                          )}
                          options={themeOptions}
                          onChange={this.selectTheme}
                          value={themeOption}
                        />

                        <Select
                          label={i18n.translate(
                            'AdminHTML.Recommendation.WidgetDashboard.filter_Type_Label'
                          )}
                          placeholder={i18n.translate(
                            'AdminHTML.Recommendation.WidgetDashboard.filter_Type_Placeholder'
                          )}
                          options={recommendationTypeOptions}
                          onChange={this.selectType}
                          value={recommendationTypeOption}
                        />
                      </Stack>
                    </div>
                  </Popover.Section>
                </Popover.Pane>

                <Popover.Pane fixed>
                  <Popover.Section>
                    <Stack alignment="baseline" distribution="equalSpacing">
                      <Button plain onClick={this.clearAllFilter}>
                        {i18n.translate('AdminHTML.Common.Action.clearAll')}
                      </Button>
                      <Button primary onClick={this.handleApply}>
                        {i18n.translate('AdminHTML.Common.Action.apply')}
                      </Button>
                    </Stack>
                  </Popover.Section>
                </Popover.Pane>
              </Popover>
            </div>
            <div className="action refresh">
              <Button plain onClick={syncWidgets} icon={RefreshIcon}>
                {i18n.translate('AdminHTML.Recommendation.WidgetDashboard.refresh_Label')}
              </Button>
            </div>
          </div>
        </div>
        {this.renderApplyFilter()}
      </div>
    );
  }

  // Toggle state to show/hide the popover
  togglePopoverActive = () => {
    this.setState(({ popoverActive }) => {
      return { popoverActive: !popoverActive };
    });
  };

  // Handle cancel/close popover
  closePopover = () => {
    // const { themeOption = '', recommendationTypeOption = '' } = this.props;
    //reset value
    // this.setState({ themeOption, recommendationTypeOption });

    this.togglePopoverActive();
  };

  // Handle select theme
  selectTheme = value => {
    this.setState({ themeOption: value });
  };

  // Handle select recommendation type
  selectType = value => {
    this.setState({ recommendationTypeOption: value });
  };

  // Handle clear filter data
  clearAllFilter = () => {
    const { filterOptions, handleFilter } = this.props;
    const { themeOption = '', recommendationTypeOption = '' } = filterOptions;
    //reset value
    this.setState({ themeOption: '', recommendationTypeOption: '' });
    if (themeOption !== '' || recommendationTypeOption !== '') {
      handleFilter({ themeOption: '', recommendationTypeOption: '' });
    }
    this.togglePopoverActive();
  };

  // Handle apply all
  handleApply = () => {
    const { filterOptions, handleFilter } = this.props;
    const {
      themeOption: themeOptionProps = '',
      recommendationTypeOption: recommendationTypeOptionProps = ''
    } = filterOptions;
    const { themeOption = '', recommendationTypeOption = '' } = this.state;
    // Apply value
    if (
      themeOption !== themeOptionProps ||
      recommendationTypeOption !== recommendationTypeOptionProps
    ) {
      handleFilter({ themeOption, recommendationTypeOption });
    }
    this.togglePopoverActive();
  };

  disambiguateLabel = type => {
    const { i18n, filterOptions } = this.props;
    switch (type) {
      case 'theme': {
        const { themeOption } = filterOptions;
        const { themeOptions } = this.state;

        const label = _.find(themeOptions, option => option.value === themeOption).label;
        return i18n.translate('AdminHTML.Recommendation.WidgetDashboard.filter_Theme_Display', {
          nt_theme: label
        });
      }
      case 'type': {
        const { recommendationTypeOption } = filterOptions;
        const { recommendationTypeOptions } = this.state;

        const label = _.find(
          recommendationTypeOptions,
          option => option.value === recommendationTypeOption
        ).label;
        return i18n.translate('AdminHTML.Recommendation.WidgetDashboard.filter_Type_Display', {
          nt_type: label
        });
      }
      default: {
        return;
      }
    }
  };
}

export default withI18n()(FiltersBar);
